import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AutoAwesome, Edit, Quiz, Search, Visibility } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import api from "../../api";
import ConvertToMcqDialog from "./components/ConvertToMcqDialog";

/**
 * Papers (PDF) page — examplify-style list of imported PDF papers.
 * Browse uploaded PDF papers and open them in the viewer.
 */
const QuestionBankPapers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const page = Math.max(parseInt(searchParams.get("page") || "1", 10), 1);
  const perPage = Math.max(parseInt(searchParams.get("per_page") || "15", 10), 1);
  const searchQuery = searchParams.get("search") || "";
  const status = searchParams.get("status") || "";
  const subject = searchParams.get("subject") || "";

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [subjects, setSubjects] = useState([]);

  const [generateTarget, setGenerateTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateCount, setGenerateCount] = useState(10);
  const [generateDifficulty, setGenerateDifficulty] = useState("Mixed");
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [generationJobId, setGenerationJobId] = useState(null);
  const pollRef = useRef(null);

  const getRequestedCount = (paper) => {
    const extractedCount = parseInt(paper?.parsed_question_count || 0, 10);

    if (Number.isFinite(extractedCount) && extractedCount > 0) {
      return Math.min(100, extractedCount);
    }

    return 10;
  };

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  useEffect(() => {
    api
      .get("/admin/question-bank/exam-subjects")
      .then((res) => {
        const list = res.data?.data || [];
        setSubjects(Array.isArray(list) ? list.filter((s) => s.key) : []);
      })
      .catch(() => {});
  }, []);

  const listSearchString = searchParams.toString();

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next);
  };

  useEffect(() => {
    const fetchPapers = async () => {
      setLoading(true);
      try {
        const res = await api.get("/admin/question-bank/import/papers", {
          params: {
            page,
            per_page: perPage,
            search: searchQuery || undefined,
            status: status || undefined,
            subject: subject || undefined,
          },
        });
        const data = res.data?.data;
        setPapers(data?.data || []);
        setTotal(data?.total || 0);
      } catch (error) {
        setPapers([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    fetchPapers();
  }, [page, perPage, searchQuery, status, subject]);

  const handleSearch = () => {
    updateParams({ search: searchInput || null, page: 1 });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleOpenGenerate = (paper) => {
    setGenerateTarget(paper);
    setGenerateCount(getRequestedCount(paper));
    setGenerateDifficulty("Mixed");
    setGenerationError(null);
    setGenerationJobId(null);
    setGenerating(false);
    setGenerateOpen(true);
  };

  const startPolling = (importId, jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(
          `/admin/question-bank/import/${importId}/similar-paper-status?job_id=${jobId}`
        );
        const s = res.data?.data;
        if (s?.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerateOpen(false);
          navigate(`/admin/question-bank/similar-papers?job=${jobId}`);
        } else if (s?.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerationError(s?.error_message || "Generation failed.");
          setGenerating(false);
        }
      } catch { /* ignore transient poll errors */ }
    }, 3000);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationError(null);
    try {
      const res = await api.post(
        `/admin/question-bank/import/${generateTarget.id}/generate-similar-paper`,
        { count: generateCount, difficulty: generateDifficulty, source: "ai", async: true }
      );
      const jobId = res.data?.data?.similar_paper_job_id;
      if (!jobId) throw new Error("No job ID returned");
      setGenerationJobId(jobId);
      startPolling(generateTarget.id, jobId);
    } catch (err) {
      setGenerationError(err.response?.data?.message || "Failed to start generation.");
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#3B2A9F" }}>
            Imported Papers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse uploaded PDF papers and open them in the viewer.
          </Typography>
        </Box>

        {/* Search bar */}
        <Box sx={{ px: 3, pb: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search papers..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            sx={{
              textTransform: "none",
              background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
              "&:hover": {
                background: "linear-gradient(90deg, #3B2A9F 0%, #8b1a28 100%)",
              },
              px: 3,
              height: 40,
            }}
          >
            Search
          </Button>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => updateParams({ status: e.target.value || null, page: 1 })}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Subject</InputLabel>
            <Select
              value={subject}
              label="Subject"
              onChange={(e) => updateParams({ subject: e.target.value || null, page: 1 })}
            >
              <MenuItem value="">All subjects</MenuItem>
              {subjects.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ px: 3, pb: 3, display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading papers...
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Paper Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Extracted</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Uploaded At</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {papers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 4, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        No PDF papers found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  papers.map((paper) => (
                    <TableRow
                      key={paper.id}
                      hover
                      sx={{ "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {paper.source_filename}
                        </Typography>
                        {paper.exam_title && (
                          <Typography variant="caption" color="text.secondary">
                            {paper.exam_title}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {paper.exam_subject || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {paper.parsed_question_count || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                          {paper.status || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {paper.created_at
                            ? format(new Date(paper.created_at), "MMMM do, yyyy h:mm a")
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AutoAwesome fontSize="small" />}
                            onClick={() => handleOpenGenerate(paper)}
                            disabled={paper.status !== "completed"}
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              borderColor: "#e0e0e0",
                              color: "text.primary",
                              "&:hover": { borderColor: "#3B2A9F", color: "#3B2A9F" },
                              "&:disabled": { opacity: 0.4 },
                            }}
                          >
                            Similar Paper
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Quiz fontSize="small" />}
                            onClick={() => setConvertTarget(paper)}
                            disabled={paper.status !== "completed"}
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              borderColor: "#e0e0e0",
                              color: "text.primary",
                              "&:hover": { borderColor: "#3B2A9F", color: "#3B2A9F" },
                              "&:disabled": { opacity: 0.4 },
                            }}
                          >
                            Convert to MCQ
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Edit fontSize="small" />}
                            onClick={() => navigate(`/admin/question-bank/import/${paper.id}/review`)}
                            disabled={paper.status !== "completed"}
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              borderColor: "#e0e0e0",
                              color: "text.primary",
                              "&:hover": { borderColor: "#6366f1", color: "#6366f1" },
                              "&:disabled": { opacity: 0.4 },
                            }}
                          >
                            Edit Questions
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility fontSize="small" />}
                            onClick={() =>
                              navigate(`/admin/question-bank/papers/${paper.id}`, {
                                state: { listSearch: listSearchString },
                              })
                            }
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              borderColor: "#e0e0e0",
                              color: "text.primary",
                              "&:hover": {
                                borderColor: "#bdbdbd",
                                bgcolor: "#f5f5f5",
                              },
                            }}
                          >
                            View PDF
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && (
          <TablePagination
            component="div"
            count={total}
            page={page - 1}
            onPageChange={(_, nextPage) => updateParams({ page: nextPage + 1 })}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => updateParams({ per_page: e.target.value, page: 1 })}
            rowsPerPageOptions={[15, 25, 50]}
          />
        )}
      </Paper>

      <Dialog
        open={generateOpen}
        onClose={() => !generating && setGenerateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Generate Similar Paper</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {generateTarget && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Source: <strong>{generateTarget.source_filename}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                AI rewrites the questions while the backend keeps the original paper format, layout cues,
                extracted structure, and image workflow aligned with the imported PDF.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                After generation completes, you will be redirected to the review page to inspect the
                result and then save it to the question bank.
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Number of questions"
              type="number"
              size="small"
              value={generateCount}
              onChange={(e) =>
                setGenerateCount(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))
              }
              inputProps={{ min: 1, max: 100 }}
              fullWidth
              helperText={
                generateTarget?.parsed_question_count
                  ? `Source extracted count: ${generateTarget.parsed_question_count}`
                  : "Backend uses the extracted paper structure as the reference."
              }
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={generateDifficulty}
                label="Difficulty"
                onChange={(e) => setGenerateDifficulty(e.target.value)}
              >
                {["Mixed", "Easy", "Medium", "Hard"].map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" variant="outlined">
              Review-first flow: generate now, review on the similar-paper page, then use
              <strong> Save to Question Bank</strong> after checking answers and diagrams.
            </Alert>
            {generateTarget?.parsed_question_count > 0 && generateCount > generateTarget.parsed_question_count && (
              <Alert severity="warning" variant="outlined">
                This paper currently has {generateTarget.parsed_question_count} extracted questions. The
                generator keeps the source structure, so requests above that may be capped by the backend.
              </Alert>
            )}
            {generationJobId && !generationError && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Generating in background&hellip; You will be redirected to review when complete.
              </Alert>
            )}
            {generationError && <Alert severity="error">{generationError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setGenerateOpen(false)}
            disabled={generating}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating || !!generationJobId}
            startIcon={
              generating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesome />
              )
            }
            sx={{
              textTransform: "none",
              background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
              "&:hover": { background: "linear-gradient(90deg, #3B2A9F 0%, #8b1a28 100%)" },
            }}
          >
            {generating ? "Generating…" : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConvertToMcqDialog
        open={Boolean(convertTarget)}
        onClose={() => setConvertTarget(null)}
        importId={convertTarget?.id}
        title={convertTarget?.source_filename}
        questionCount={convertTarget?.questions_imported}
        defaultCategoryId={convertTarget?.target_category_id || ""}
      />
    </Box>
  );
};

export default QuestionBankPapers;
